import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Settings,
  Maximize2,
  Minimize2,
  Headphones,
  Monitor,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ReadingSettings } from "@/components/player/ReadingSettings";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SubtitlePanel } from "@/components/player/SubtitlePanel";
import type { SubtitleLine, FuriganaSegment } from "@/types/subtitle";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useMembershipStatus } from "@/hooks/useMembershipStatus";
import { useTrialSeries } from "@/hooks/useTrialSeries";
import { useIsMobile } from "@/hooks/use-mobile";
import { readPlayerUiSettings, writePlayerUiSettings } from "@/lib/player-ui-settings";

interface AudioData {
  id: string;
  title: string;
  description: string | null;
  audio_url: string;
  thumbnail_url: string | null;
  jlpt_level: string;
  category: string;
  duration: number;
  series_id?: string | null;
  is_premium?: boolean | null;
  is_published?: boolean | null;
}

interface RawSubtitle {
  id: string;
  start_time: number;
  end_time: number;
  japanese: string;
  chinese: string;
  romaji: string | null;
  furigana: unknown;
  sort_order: number | null;
}

function parseFurigana(raw: unknown): FuriganaSegment[] | undefined {
  if (!raw) return undefined;

  const normalizeOne = (item: unknown): FuriganaSegment | null => {
    if (typeof item === "object" && item !== null) {
      const obj = item as Record<string, unknown>;
      const text = String(
        obj.text ??
          obj.word ??
          obj.surface_form ??
          obj.surface ??
          ""
      ).trim();
      const readingRaw = obj.reading ?? obj.kana ?? obj.pronunciation;
      const reading = readingRaw ? String(readingRaw).trim() : undefined;
      if (!text) return null;
      return { text, reading };
    }
    const text = String(item ?? "").trim();
    if (!text) return null;
    return { text };
  };

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const mapped = parsed.map(normalizeOne).filter((item): item is FuriganaSegment => !!item);
        return mapped.length > 0 ? mapped : undefined;
      }
    } catch {
      const single = normalizeOne(raw);
      return single ? [single] : undefined;
    }
  }
  if (Array.isArray(raw)) {
    const mapped = raw.map(normalizeOne).filter((item): item is FuriganaSegment => !!item);
    return mapped.length > 0 ? mapped : undefined;
  }
  return undefined;
}

export default function AudioPlayer() {
  const { audioId } = useParams<{ audioId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { data: membership } = useMembershipStatus(user?.id);
  const { data: trial } = useTrialSeries();
  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const initialUiSettings = useMemo(() => readPlayerUiSettings(), []);

  const [audio, setAudio] = useState<AudioData | null>(null);
  const [subtitles, setSubtitles] = useState<SubtitleLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(initialUiSettings.volume ?? 1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Subtitle display settings
  const [showFurigana, setShowFurigana] = useState(initialUiSettings.showFurigana ?? true);
  const [showRomaji, setShowRomaji] = useState(initialUiSettings.showRomaji ?? false);
  const [showChinese, setShowChinese] = useState(initialUiSettings.showChinese ?? true);
  const [hideUnderline, setHideUnderline] = useState(initialUiSettings.hideUnderline ?? false);
  const [hideGrammarBackground, setHideGrammarBackground] = useState(initialUiSettings.hideGrammarBackground ?? false);
  const [hideShadowingSpotlight, setHideShadowingSpotlight] = useState(initialUiSettings.hideShadowingSpotlight ?? false);
  const [showOnlyChinese, setShowOnlyChinese] = useState(initialUiSettings.showOnlyChinese ?? false);
  const [highlightNewWords, setHighlightNewWords] = useState(initialUiSettings.highlightNewWords ?? true);
  const [fontSize, setFontSize] = useState(initialUiSettings.fontSize ?? 1);
  const [blurStrength, setBlurStrength] = useState(initialUiSettings.blurStrength ?? 0);
  const [theme, setTheme] = useState(initialUiSettings.theme ?? "system");
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [focusSubtitles, setFocusSubtitles] = useState(true);

  // Apply theme
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    if (audioId) {
      fetchAudioData();
    }
  }, [audioId, membership?.isPremium, membership?.isAdmin, trial?.trialSeriesId]);

  useEffect(() => {
    const normalizedTheme = theme === "light" || theme === "dark" ? theme : "system";
    writePlayerUiSettings({
      showFurigana,
      showRomaji,
      showChinese,
      hideUnderline,
      hideGrammarBackground,
      hideShadowingSpotlight,
      showOnlyChinese,
      highlightNewWords,
      fontSize,
      blurStrength,
      volume,
      theme: normalizedTheme,
    });
  }, [
    blurStrength,
    fontSize,
    hideGrammarBackground,
    hideShadowingSpotlight,
    hideUnderline,
    highlightNewWords,
    showChinese,
    showFurigana,
    showOnlyChinese,
    showRomaji,
    theme,
    volume,
  ]);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
    audioRef.current.muted = isMuted || volume <= 0;
  }, [isMuted, volume, audio?.id]);

  const fetchAudioData = async () => {
    if (!audioId) return;

    setLoading(true);
    try {
      // Fetch audio
      const { data: audioData, error: audioError } = await supabase
        .from("audios")
        .select("*")
        .eq("id", audioId)
        .maybeSingle();

      if (audioError) throw audioError;
      if (!audioData) {
        toast({ title: "音频不存在", variant: "destructive" });
        navigate("/");
        return;
      }

      if (membership) {
        const isPremium = !!membership.isPremium;
        const isAdmin = !!membership.isAdmin;
        const trialSeriesId = (trial as any)?.trialSeriesId as string | null | undefined;

        if (!isAdmin && audioData.series_id) {
          const { data: seriesRow } = await supabase
            .from("series")
            .select("is_hidden")
            .eq("id", audioData.series_id)
            .maybeSingle();
          if ((seriesRow as any)?.is_hidden) {
            toast({ title: "内容不可用", variant: "destructive" });
            navigate("/");
            return;
          }
        }

        if (!isPremium) {
          if (trialSeriesId) {
            if (!audioData.series_id || audioData.series_id !== trialSeriesId) {
              toast({ title: "需要会员", description: "普通用户仅可试看指定系列", variant: "destructive" });
              navigate("/profile");
              return;
            }
          } else {
            if (audioData.is_premium) {
              toast({ title: "需要会员", description: "该内容为会员专享", variant: "destructive" });
              navigate("/profile");
              return;
            }
          }
        }
      }

      setAudio(audioData);

      // Fetch subtitles
      const { data: subtitleData, error: subtitleError } = await supabase
        .from("audio_subtitles")
        .select("*")
        .eq("audio_id", audioId)
        .order("sort_order", { ascending: true });

      if (subtitleError) throw subtitleError;

      const parsedSubtitles: SubtitleLine[] = (subtitleData || []).map(
        (raw: RawSubtitle) => ({
          id: raw.id,
          startTime: Number(raw.start_time),
          endTime: Number(raw.end_time),
          japanese: raw.japanese,
          chinese: raw.chinese,
          romaji: raw.romaji || undefined,
          furigana: parseFurigana(raw.furigana),
        })
      );

      setSubtitles(parsedSubtitles);
    } catch (error) {
      console.error("Error fetching audio:", error);
      toast({ title: "加载音频失败", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const handleSliderSeek = (value: number[]) => {
    handleSeek(value[0]);
  };

  const skipBackward = () => {
    handleSeek(Math.max(0, currentTime - 10));
  };

  const skipForward = () => {
    handleSeek(Math.min(duration, currentTime + 10));
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      await containerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!audio) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">音频不存在</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "min-h-screen bg-gradient-to-b from-background to-muted/30 flex flex-col",
        isFullscreen && "fixed inset-0 z-50"
      )}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={audio.audio_url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* Header */}
      <header className="flex items-center gap-4 p-4 border-b border-border/50">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate">{audio.title}</h1>
          <p className="text-sm text-muted-foreground">
            {audio.jlpt_level} · {subtitles.length} 句字幕
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <Settings className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>字幕设置</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="flex items-center justify-between"
              onSelect={(e) => e.preventDefault()}
            >
              <span>显示振假名</span>
              <Switch
                checked={showFurigana}
                onCheckedChange={setShowFurigana}
              />
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center justify-between"
              onSelect={(e) => e.preventDefault()}
            >
              <span>显示罗马音</span>
              <Switch checked={showRomaji} onCheckedChange={setShowRomaji} />
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center justify-between"
              onSelect={(e) => e.preventDefault()}
            >
              <span>显示中文</span>
              <Switch checked={showChinese} onCheckedChange={setShowChinese} />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFocusSubtitles((v) => !v)}
          className="h-9 px-3"
        >
          <Monitor className="h-4 w-4" />
          <span className="ml-2">{focusSubtitles ? "简洁模式" : "显示封面"}</span>
        </Button>
        <Button variant="ghost" size="icon" onClick={toggleFullscreen}>
          {isFullscreen ? (
            <Minimize2 className="h-5 w-5" />
          ) : (
            <Maximize2 className="h-5 w-5" />
          )}
        </Button>
      </header>

      {/* Thumbnail & Visualizer Area */}
      {!focusSubtitles && (
        <div className="flex-shrink-0 p-6 flex justify-center">
          <div className="relative">
            {audio.thumbnail_url ? (
              <img
                src={audio.thumbnail_url}
                alt={audio.title}
                className={cn(
                  "rounded-2xl shadow-2xl object-cover",
                  isPlaying && "animate-pulse",
                  isFullscreen ? "w-48 h-48" : "w-32 h-32"
                )}
              />
            ) : (
              <div
                className={cn(
                  "rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center shadow-2xl",
                  isFullscreen ? "w-48 h-48" : "w-32 h-32"
                )}
              >
                <Headphones
                  className={cn(
                    "text-primary",
                    isFullscreen ? "h-20 w-20" : "h-12 w-12"
                  )}
                />
              </div>
            )}
            {isPlaying && (
              <div className="absolute inset-0 rounded-2xl ring-4 ring-primary/30 animate-pulse" />
            )}
          </div>
        </div>
      )}

      {/* Subtitle Panel */}
      <div className="flex-1 overflow-hidden px-4">
        <SubtitlePanel
          subtitles={subtitles}
          currentTime={currentTime}
          onSeek={handleSeek}
          showFurigana={showFurigana}
          showRomaji={showRomaji}
          showChinese={showChinese}
          videoId={audioId || ""}
          hideUnderline={hideUnderline}
          hideGrammarBackground={hideGrammarBackground}
          hideShadowingSpotlight={hideShadowingSpotlight}
          shadowingMode={false} // Audio player doesn't have shadowing mode yet
          showOnlyChinese={showOnlyChinese}
          highlightNewWords={highlightNewWords}
          setShowFurigana={setShowFurigana}
          setShowRomaji={setShowRomaji}
          setShowChinese={setShowChinese}
          setHideUnderline={setHideUnderline}
          setHideGrammarBackground={setHideGrammarBackground}
          setHideShadowingSpotlight={setHideShadowingSpotlight}
          setShadowingMode={() => {}} // No-op
          setShowOnlyChinese={setShowOnlyChinese}
          setHighlightNewWords={setHighlightNewWords}
          theme={theme}
          setTheme={setTheme}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onGoHome={() => navigate("/")}
          fontSize={fontSize}
          blurStrength={blurStrength}
        />
      </div>

      {/* Playback Controls */}
      <div className="flex-shrink-0 p-4 pb-6 space-y-4 bg-gradient-to-t from-background to-transparent">
        {/* Progress bar */}
        <div className="space-y-2">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={0.1}
            onValueChange={handleSliderSeek}
            className="cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          {/* Volume */}
          <div className="flex items-center gap-2 w-32">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={toggleMute}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <Slider
              value={[isMuted ? 0 : volume]}
              max={1}
              step={0.01}
              onValueChange={handleVolumeChange}
              className="flex-1"
            />
          </div>

          {/* Main controls */}
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={skipBackward}
          >
            <SkipBack className="h-5 w-5" />
          </Button>
          <Button
            size="icon"
            className="h-14 w-14 rounded-full"
            onClick={togglePlay}
          >
            {isPlaying ? (
              <Pause className="h-6 w-6" />
            ) : (
              <Play className="h-6 w-6 ml-0.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            onClick={skipForward}
          >
            <SkipForward className="h-5 w-5" />
          </Button>

          {/* Spacer to balance layout */}
          <div className="w-32" />
        </div>
      </div>

      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent
          className={cn(
            "z-[120] p-0 bg-transparent border-0 shadow-none focus:outline-none [&>button]:hidden",
            "w-[min(96vw,420px)] left-1/2 bottom-2 top-auto -translate-x-1/2 translate-y-0 max-h-[88dvh] overflow-visible",
            "sm:w-auto sm:max-w-[360px] sm:max-h-none sm:overflow-visible sm:left-[50%] sm:top-[50%] sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%]",
          )}
        >
          <ReadingSettings
            showFurigana={showFurigana}
            setShowFurigana={setShowFurigana}
            showRomaji={showRomaji}
            setShowRomaji={setShowRomaji}
            showChinese={showChinese}
            setShowChinese={setShowChinese}
            hideUnderline={hideUnderline}
            setHideUnderline={setHideUnderline}
            hideGrammarBackground={hideGrammarBackground}
            setHideGrammarBackground={setHideGrammarBackground}
            hideShadowingSpotlight={hideShadowingSpotlight}
            setHideShadowingSpotlight={setHideShadowingSpotlight}
            showOnlyChinese={showOnlyChinese}
            setShowOnlyChinese={setShowOnlyChinese}
            highlightNewWords={highlightNewWords}
            setHighlightNewWords={setHighlightNewWords}
            theme={theme}
            setTheme={setTheme}
            onClose={() => setIsSettingsOpen(false)}
            fontSize={fontSize}
            setFontSize={setFontSize}
            blurStrength={blurStrength}
            setBlurStrength={setBlurStrength}
            volume={volume}
            setVolume={(next) => handleVolumeChange([next])}
            isMobile={isMobile}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
